/**
 * Table Size Picker
 * 
 * Visual grid picker for selecting table dimensions before insertion.
 */

import { useState, useCallback } from 'react';

interface TableSizePickerProps {
  onSelect: (rows: number, cols: number) => void;
  onClose: () => void;
  maxRows?: number;
  maxCols?: number;
}

export function TableSizePicker({ 
  onSelect, 
  onClose, 
  maxRows = 8, 
  maxCols = 8 
}: TableSizePickerProps) {
  const [hoveredCell, setHoveredCell] = useState<{ row: number; col: number } | null>(null);
  
  const handleCellClick = useCallback((row: number, col: number) => {
    onSelect(row + 1, col + 1); // Convert 0-indexed to 1-indexed
    onClose();
  }, [onSelect, onClose]);
  
  const handleMouseLeave = useCallback(() => {
    setHoveredCell(null);
  }, []);
  
  return (
    <div 
      className="absolute top-full left-0 mt-1 p-2 bg-card border border-border rounded-lg shadow-lg z-50"
      onMouseLeave={handleMouseLeave}
    >
      <div className="text-xs text-muted-foreground mb-2 text-center">
        {hoveredCell ? `${hoveredCell.row + 1} × ${hoveredCell.col + 1}` : 'Select size'}
      </div>
      <div 
        className="grid gap-0.5"
        style={{ gridTemplateColumns: `repeat(${maxCols}, 1fr)` }}
      >
        {Array.from({ length: maxRows * maxCols }).map((_, index) => {
          const row = Math.floor(index / maxCols);
          const col = index % maxCols;
          const isHighlighted = hoveredCell && row <= hoveredCell.row && col <= hoveredCell.col;
          
          return (
            <button
              key={index}
              className={`w-4 h-4 border rounded-sm transition-colors ${
                isHighlighted 
                  ? 'bg-primary border-primary' 
                  : 'bg-muted border-border hover:border-primary/50'
              }`}
              onMouseEnter={() => setHoveredCell({ row, col })}
              onClick={() => handleCellClick(row, col)}
              aria-label={`${row + 1} rows by ${col + 1} columns`}
            />
          );
        })}
      </div>
      <div className="mt-2 pt-2 border-t border-border">
        <button
          onClick={onClose}
          className="w-full text-xs text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
